// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Home from './pages/Home';
import SignIn from './pages/SignIn';
import Navbar from './components/NavBar';
import Footer from './components/Footer';
import RegistrationForm from './components/RegistrationForm';
import Dashboard from './components/Dashboard';
import OwnerDashboard from './pages/OwnerDashboard';
import { AuthProvider, useAuth } from './context/AuthContext';
import TherapistDashboard from './pages/TherapistDashboard';
import { Center, Spinner } from '@chakra-ui/react';
import ReceptionistDashboard from './pages/ReceptionistDashboard';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Support from './pages/Support';
import { ContentProvider } from './context/ContentContext';
import AuthCallback from './pages/AuthCallback';
import ParentDashboard from './components/ParentDashboard';
import ParentSignIn from './pages/ParentSignIn';
import MakeupSessionMigration from './components/MakeupSessionMigration';
import SessionNotesRepair from './pages/SessionNotesRepair';
import supabase from './supabaseClient';

// Email confirmation handler component
const EmailConfirmationHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const hashParams = new URLSearchParams(location.hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');
    const type = hashParams.get('type');

    if (!(type === 'signup' && accessToken && refreshToken)) {
      return;
    }

    const clearAuthHash = () => {
      window.history.replaceState(null, '', `${location.pathname}${location.search}`);
    };

    const handleEmailConfirmation = async () => {
      try {
        console.log('Processing email confirmation...');

        // Never interrupt an active session (e.g. owner actively using the app).
        const {
          data: { session: existingSession },
        } = await supabase.auth.getSession();
        if (existingSession?.user) {
          clearAuthHash();
          return;
        }

        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });

        if (error) throw error;

        const {
          data: { user },
        } = await supabase.auth.getUser();

        if (!user) {
          clearAuthHash();
          return;
        }

        console.log('User confirmed:', user.id);

        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('role')
          .eq('id', user.id)
          .single();

        if (!userError && userData) {
          console.log('User role:', userData.role);

          await supabase.auth.signOut({ scope: 'local' });
          console.log('User signed out after email confirmation');
          clearAuthHash();

          setTimeout(() => {
            if (userData.role === 'parent') {
              navigate('/parent-signin', { replace: true });
            } else if (userData.role === 'therapist') {
              navigate('/sign-in', { replace: true });
            } else {
              navigate('/', { replace: true });
            }
          }, 1500);
        } else {
          console.error('Error getting user role:', userError);
          await supabase.auth.signOut({ scope: 'local' });
          clearAuthHash();
          navigate('/', { replace: true });
        }
      } catch (error) {
        console.error('Error confirming email:', error);
        await supabase.auth.signOut({ scope: 'local' });
        clearAuthHash();
        navigate('/', { replace: true });
      }
    };

    handleEmailConfirmation();
  }, [location.hash, navigate]);

  return null; // This component doesn't render anything
};

const dashboardPathForRole = (role) => {
  switch (role) {
    case 'owner':
      return '/owner-dashboard';
    case 'therapist':
      return '/therapist-dashboard';
    case 'receptionist':
      return '/receptionist-dashboard';
    case 'parent':
      return '/parent-dashboard';
    default:
      return '/';
  }
};

const ProtectedRoute = ({ children }) => {
  const { user, loading, profile } = useAuth();
  const location = useLocation();

  // Role lives on merged user state after fetchProfile; during token refresh session.user briefly has no role — use profile fallback
  const role = user?.role ?? profile?.role;

  // Handle loading state
  if (loading) {
    return <Center h="100vh"><Spinner /></Center>;
  }

  // If no user, redirect to sign-in
  if (!user) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  // If user exists but no role yet, show loading
  if (!role) {
    return <Center h="100vh"><Spinner /></Center>;
  }

  // Owner dashboard is owner-only (therapist / parent / receptionist cannot access)
  if (location.pathname === '/owner-dashboard' && role !== 'owner') {
    return <Navigate to={dashboardPathForRole(role)} replace />;
  }

  // Receptionist dashboard is not for therapists or parents
  if (
    location.pathname === '/receptionist-dashboard' &&
    (role === 'therapist' || role === 'parent')
  ) {
    return <Navigate to={dashboardPathForRole(role)} replace />;
  }

  // Redirect to appropriate dashboard based on role (/dashboard must never show staff-only widgets to parents)
  if (location.pathname === '/dashboard') {
    if (role === 'owner') {
      return <Navigate to="/owner-dashboard" replace />;
    }
    if (role === 'therapist') {
      return <Navigate to="/therapist-dashboard" replace />;
    }
    if (role === 'receptionist') {
      return <Navigate to="/receptionist-dashboard" replace />;
    }
    if (role === 'parent') {
      return <Navigate to="/parent-dashboard" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <ContentProvider>
        <Router>
          <EmailConfirmationHandler />
          <Navbar />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/sign-in" element={<SignIn />} />
            <Route path="/parent-signin" element={<ParentSignIn/>} />
            <Route path="/auth/callback" element={<AuthCallback/>}/>
            <Route path="/register" element={<RegistrationForm />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/support" element={<Support />} />
            <Route 
              path="/owner-dashboard" 
              element={
                <ProtectedRoute>
                  <OwnerDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/therapist-dashboard" 
              element={
                <ProtectedRoute>
                  <TherapistDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/parent-dashboard" 
              element={
                <ProtectedRoute>
                  <ParentDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/receptionist-dashboard" 
              element={
                <ProtectedRoute>
                  <ReceptionistDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/makeup-migration" 
              element={
                <ProtectedRoute>
                  <MakeupSessionMigration />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/notes-repair" 
              element={
                <ProtectedRoute>
                  <SessionNotesRepair />
                </ProtectedRoute>
              } 
            />
          </Routes>
          <Footer />
        </Router>
      </ContentProvider>
    </AuthProvider>
  );
}

export default App;